import { motion } from 'motion/react'
import { BookMeButton } from '../components/BookingGuide'
import { Page } from '../components/Page'
import { Reveal } from '../components/Reveal'
import type { SiteProfile } from '../types'

const viewport = { once: true, amount: 0.15 }

function SectionHeading({ number, children, id }: { number: string; children: string; id: string }) {
  return (
    <motion.div className="resume-heading" initial={{ opacity: 0, x: -28 }} whileInView={{ opacity: 1, x: 0 }} viewport={viewport} transition={{ duration: 0.62, ease: [0.22, 1, 0.36, 1] }}>
      <span aria-hidden="true">{number}</span>
      <h2 id={id}>{children}</h2>
      <i aria-hidden="true" />
    </motion.div>
  )
}

export function AboutPage({ profile }: { profile: SiteProfile }) {
  const { resume } = profile

  return (
    <Page className="about-page">
      <section className="about-hero">
        <div className="about-copy">
          <Reveal><h1>{profile.aboutTitle}</h1></Reveal>
          <Reveal delay={0.1}><p className="about-subtitle">{profile.aboutSubtitle}</p></Reveal>
          <div className="about-rule" />
          <h2>Professional summary</h2>
          <p>{resume.professionalSummary}</p>
        </div>
      </section>

      <section className="resume-section" aria-labelledby="experience-heading">
        <SectionHeading number="01" id="experience-heading">Professional experience</SectionHeading>
        {resume.experience.map((job) => (
          <motion.article className="resume-job" key={`${job.role}-${job.company}`} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewport} transition={{ duration: 0.68, ease: [0.22, 1, 0.36, 1] }}>
            <div className="resume-job-overview">
              <p className="resume-kicker">Current role</p>
              <h3>{job.role}</h3>
              <p className="resume-company">{job.company}</p>
              <p className="resume-dates">{job.dates}</p>
              <p className="resume-focus">{job.focus}</p>
            </div>
            <ul className="resume-bullets">
              {job.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
          </motion.article>
        ))}
      </section>

      <section className="resume-section" aria-labelledby="consulting-heading">
        <SectionHeading number="02" id="consulting-heading">AI consulting, workshops & solution development</SectionHeading>
        <ol className="consulting-list">
          {resume.consultingHighlights.map((highlight, index) => (
            <motion.li key={highlight} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewport} transition={{ duration: 0.56, delay: index * 0.025, ease: [0.22, 1, 0.36, 1] }}>
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              <p>{highlight}</p>
            </motion.li>
          ))}
        </ol>
      </section>

      <section className="resume-section" aria-labelledby="expertise-heading">
        <SectionHeading number="03" id="expertise-heading">Technical expertise</SectionHeading>
        <div className="expertise-list">
          {resume.expertise.map((item, index) => (
            <motion.article className="expertise-item" key={item.title} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewport} transition={{ duration: 0.58, delay: (index % 2) * 0.04, ease: [0.22, 1, 0.36, 1] }}>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="resume-section" aria-labelledby="education-heading">
        <SectionHeading number="04" id="education-heading">Education</SectionHeading>
        <div className="education-list">
          {resume.education.map((item) => (
            <motion.article key={item.degree} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewport} transition={{ duration: 0.58, ease: [0.22, 1, 0.36, 1] }}>
              <h3>{item.degree}</h3>
              <p><strong>{item.institution}</strong><span aria-hidden="true">•</span>{item.location}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="about-closing">
        <p>{profile.closingStatement.split(' ').map((word, index) => index === 2 ? <em key={`${word}-${index}`}>{word} </em> : `${word} `)}</p>
        <BookMeButton className="about-book" />
      </section>
    </Page>
  )
}
